'use client';

import * as React from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  CreditCard,
  Gift,
  Languages,
  Lightbulb,
  Lock,
  LogOut,
  Moon,
  Settings2,
  Shield,
  SlidersHorizontal,
  Stethoscope,
  Sun,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { ADMIN_ROUTES, MANAGE_ROUTES, PARTNER_ADMIN_ROUTES, RX_ROUTES } from '@/config/router';
import { LanguageSwitcher } from '@/components/language-switcher';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FastSearchCompact } from '@/components/ui/fast-search';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountManagerHeaderControls } from '@/components/ui/account-manager-widget';
import { NotificationBell } from '@/components/notifications/notification-bell';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { cn } from '@/lib/utils';

function AppHeaderLogo({ logo, logoDark, alt }: { logo: string; logoDark?: string; alt: string }) {
  // Incomplete/neutral partner brand: render nothing rather than a broken <img>.
  if (!logo) return null;

  return (
    <>
      <img src={logo} alt={alt} className={cn('crm-app-header__logo', logoDark && 'dark:hidden')} />
      {logoDark ? (
        <img src={logoDark} alt={alt} className="crm-app-header__logo hidden dark:md:block" />
      ) : null}
    </>
  );
}

function MobileThemeToggleItem() {
  const [isDark, setIsDark] = React.useState(() => document.documentElement.classList.contains('dark'));

  const handleToggleTheme = (e: React.MouseEvent) => {
    e.preventDefault();
    const next = !isDark;
    setIsDark(next);
    if (next) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <DropdownMenuItem onClick={handleToggleTheme} onSelect={(e) => e.preventDefault()}>
      {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
      <span className="text-sm">{isDark ? 'Light mode' : 'Dark mode'}</span>
    </DropdownMenuItem>
  );
}

function MobileLanguageMenu() {
  const { i18n } = useTranslation();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="text-sm">
        <Languages className="mr-2 h-4 w-4" />
        <span className="flex-1 text-sm">Language</span>
        <span className="text-muted-foreground text-xs">{i18n.language === 'fr' ? 'FR' : 'EN'}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={i18n.language} onValueChange={(v) => i18n.changeLanguage(v)}>
          <DropdownMenuRadioItem value="en" className="text-sm">
            🇬🇧 English
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="fr" className="text-sm">
            🇫🇷 Français
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export interface AppHeaderProps {
  searchPlaceholder: string;
  displayReferral: boolean;
  isAccountManager?: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isPartnerAdmin: boolean;
  /** `rx_ops` / `rx_admin` holder (not a platform admin) — gets a TagadaRx entry. */
  isRxStaff?: boolean;
  whitelabelOwnership: string | null | undefined;
  onSignOut: () => void;
  onRequestFeature?: () => void;
  showRequestFeature?: boolean;
  hideSidebarTrigger?: boolean;
  hideSearch?: boolean;
}

export function AppHeader({
  searchPlaceholder,
  displayReferral,
  isAccountManager = false,
  isAdmin,
  isSuperAdmin,
  isPartnerAdmin,
  isRxStaff = false,
  whitelabelOwnership,
  onSignOut,
  onRequestFeature,
  showRequestFeature = false,
  hideSidebarTrigger = false,
  hideSearch = false,
}: AppHeaderProps) {
  const { t } = useTranslation('navigation');
  const { user } = useUser();
  const { orgRoutes } = useOrgNavigate();
  const branding = useEffectiveBranding();

  return (
    <header className="crm-app-header">
      <div className="crm-app-header__brand">
        {!hideSidebarTrigger ? <SidebarTrigger className="crm-app-header__sidebar-trigger" /> : null}
        <AppHeaderLogo logo={branding.logo} logoDark={branding.logoDark} alt={branding.appName} />
      </div>

      <div className="crm-app-header__center" aria-hidden={hideSearch || undefined}>
        {!hideSearch ? <FastSearchCompact placeholder={searchPlaceholder} /> : null}
      </div>

      <div className="crm-app-header__actions">
        {isAccountManager ? (
          <>
            <AccountManagerHeaderControls />
            <span className="crm-app-header__divider" aria-hidden />
          </>
        ) : null}

        <div className="crm-app-header__tools hidden md:flex">
          <LanguageSwitcher variant="header" />
          <ThemeToggle className="crm-app-header__theme-toggle" />
        </div>

        <span className="crm-app-header__divider hidden md:block" aria-hidden />

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" className="crm-app-header__avatar-btn">
              <Avatar className="size-7 rounded-md">
                <AvatarImage src={user?.imageUrl} alt={user?.fullName || 'User'} />
                <AvatarFallback className="bg-muted rounded-md text-[11px] font-medium">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <span className="sr-only">{t('toggleUserMenu')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <div className="md:hidden">
              <MobileLanguageMenu />
              <MobileThemeToggleItem />
              <DropdownMenuSeparator />
            </div>
            <DropdownMenuItem asChild>
              <Link to={orgRoutes.PROFILE()}>
                <User className="mr-2 h-4 w-4" />
                <span className="text-sm">{t('profile')}</span>
              </Link>
            </DropdownMenuItem>
            {displayReferral ? (
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.REFERRAL()}>
                  <Gift className="mr-2 h-4 w-4" />
                  <span className="text-sm">{t('earnMoney')}</span>
                </Link>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            {/* Payment methods always reachable; reports stay gated on the page (#TAG-1807). */}
            <DropdownMenuItem asChild>
              <Link to={`${orgRoutes.BILLING()}?tab=payment-methods`}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span className="text-sm">{t('billing')}</span>
              </Link>
            </DropdownMenuItem>
            {isAdmin ? (
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.SETTINGS()}>
                  <Settings2 className="mr-2 h-4 w-4" />
                  <span className="text-sm">{t('settings')}</span>
                </Link>
              </DropdownMenuItem>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem disabled className="pointer-events-none cursor-not-allowed opacity-50">
                      <Settings2 className="mr-2 h-4 w-4" />
                      <span className="flex-1 text-sm">{t('settings')}</span>
                      <Lock className="text-muted-foreground h-3 w-3" />
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[240px]">
                  <p>{t('adminOnly')}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {/* Staff operations. One entry — the area has its own tab nav,
                which filters itself by role. */}
            {isAccountManager || isSuperAdmin || isPartnerAdmin ? (
              <DropdownMenuItem asChild>
                <Link to={MANAGE_ROUTES.BASE}>
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  <span className="text-sm">{t('manage')}</span>
                </Link>
              </DropdownMenuItem>
            ) : null}
            {isSuperAdmin ? (
              <DropdownMenuItem asChild>
                <Link to={ADMIN_ROUTES.BASE}>
                  <Shield className="mr-2 h-4 w-4" />
                  <span className="text-sm">Admin</span>
                </Link>
              </DropdownMenuItem>
            ) : null}
            {isPartnerAdmin && whitelabelOwnership ? (
              <DropdownMenuItem asChild>
                <Link to={PARTNER_ADMIN_ROUTES(whitelabelOwnership).REVENUE}>
                  <Shield className="mr-2 h-4 w-4" />
                  <span className="text-sm">Partner Admin ({whitelabelOwnership.toUpperCase()})</span>
                </Link>
              </DropdownMenuItem>
            ) : null}
            {/* TagadaRx desk. Its own area (not an admin tab): partner-scoped
                staff land on their partner desk, everyone else on the TGD Care one. */}
            {isRxStaff || isSuperAdmin ? (
              <DropdownMenuItem asChild>
                <Link to={whitelabelOwnership ? RX_ROUTES.PARTNER(whitelabelOwnership) : RX_ROUTES.BASE}>
                  <Stethoscope className="mr-2 h-4 w-4" />
                  <span className="text-sm">
                    TagadaRx{whitelabelOwnership ? ` (${whitelabelOwnership.toUpperCase()})` : ''}
                  </span>
                </Link>
              </DropdownMenuItem>
            ) : null}
            {showRequestFeature && onRequestFeature ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onRequestFeature}>
                  <Lightbulb className="mr-2 h-4 w-4" />
                  <span className="text-sm">{t('requestFeature')}</span>
                </DropdownMenuItem>
              </>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              <span className="text-sm">{t('logOut')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
